import { Component } from '@theme/component';
import { getTruck, setTruck, onTruckChange } from '@theme/dialed-truck';
import { addLines } from '@theme/dialed-cart';
import { YmmPicker, truckLabel, defaultYear, yearsOf, kitOf } from '@theme/dialed-ymm';

/** ?pk= values (Shop page, nav chips) → Build option position */
const PACKS = { system: 0, starter: 0, blockers: 1, 'blocker-pack': 1, full: 2, 'full-build': 2 };

/**
 * <dialed-buy-module>: Year → Make → Model → Bed picker for The DIALED System.
 * Each truck fit × build is its own bundle variant (variant metafield dialed.truck_fit), so the
 * picker resolves an exact variant, then adds it with the Truck line item property and the free
 * gift (Theme settings → Shipping and offers) in the option the shopper picked.
 *
 * Data: [data-dialed-trucks] (fits) and [data-kit-variants] (variants, builds, gift variants), both
 * rendered by sections/dialed-buy-module.liquid. Copy comes from data-t-* attributes.
 */
class DialedBuyModule extends Component {
  requiredRefs = ['year', 'make', 'model', 'config', 'cta', 'ctaLabel', 'price', 'track'];

  /** @type {Array<{handle:string,label:string,make:string,model:string,years:string,bed:string,width:number,length:number,size:string}>} */
  fits = [];
  /** @type {{ builds: string[], variants: Array<{id:number,fit:string,build:string,price:number,compare:number,available:boolean}> }} */
  kit = { builds: [], variants: [] };
  state = { year: '', make: '', model: '', fit: '', build: 0 };
  /** @type {YmmPicker | null} */
  #picker = null;
  #slide = 0;
  /** True while the truck is our pick from a ?make=&model= link, until the shopper changes it */
  #guess = false;
  #abort = new AbortController();
  #unsubscribe = () => {};

  connectedCallback() {
    super.connectedCallback();
    const root = this.closest('.dl-section') ?? document;
    try {
      this.fits = JSON.parse(document.querySelector('[data-dialed-trucks]')?.textContent ?? '[]');
      this.kit = JSON.parse(root.querySelector('[data-kit-variants]')?.textContent ?? '{}');
    } catch (error) {
      console.error('[dialed-buy-module] Bad truck/variant data', error);
      return;
    }
    // The gallery's video link points at the video section; hide it until that section is on the page
    for (const link of this.querySelectorAll('[data-dl-anchor-link]')) {
      const id = link.getAttribute('href')?.replace(/^#/, '');
      if (id && !document.getElementById(id)) link.hidden = true;
    }

    const { signal } = this.#abort;
    const { year, make, model, config, cta, track } = this.refs;

    this.#picker = new YmmPicker(this.fits, { year, make, model, bed: config });
    this.#picker.bind((next) => this.#pick(next), signal);
    cta.addEventListener('click', () => this.#buy(), { signal });
    this.refs.stickyCta?.addEventListener('click', () => this.#stickyBuy(), { signal });
    this.refs.prev?.addEventListener('click', () => this.#go(this.#slide - 1), { signal });
    this.refs.next?.addEventListener('click', () => this.#go(this.#slide + 1), { signal });
    track.addEventListener('scroll', this.#onTrackScroll, { signal, passive: true });
    this.addEventListener('click', this.#onClick, { signal });
    window.addEventListener('dialed:select', this.#onSelect, { signal });
    window.addEventListener('dialed:pack', this.#onPack, { signal });
    this.#applyInitial();
    this.#unsubscribe = onTruckChange((truck) => {
      if (!truck?.fit || !this.fits.some((f) => f.handle === truck.fit)) return;
      if (truck.fit !== this.state.fit || (truck.year && truck.year !== this.state.year)) {
        this.#pick({ year: truck.year, make: truck.make, model: truck.model, fit: truck.fit }, { store: false });
      }
    });
    this.#updateGallery();
    this.#observeSticky();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#unsubscribe();
  }

  /* ---------- Truck picker ---------- */

  /**
   * Sets the truck through the Year → Make → Model → Bed picker and saves it for the visit.
   * @param {{ year?: string, make?: string, model?: string, fit?: string }} next
   * @param {{ store?: boolean, guess?: boolean }} [options]
   */
  #pick(next, { store = true, guess = false } = {}) {
    if (!this.#picker) return;
    this.#guess = guess;
    const { year, make, model, fit: handle } = this.#picker.set(next);
    const changedFit = handle !== this.state.fit;
    this.state = { ...this.state, year, make, model, fit: handle };
    const fit = this.#fit();
    if (store && fit) setTruck({ year, make: fit.make, model: fit.model, fit: fit.handle, label: truckLabel(fit, year) });
    if (changedFit) this.#go(0);
    this.#render();
  }

  #fit() {
    return this.fits.find((f) => f.handle === this.state.fit) ?? null;
  }

  #variant() {
    const build = this.kit.builds[this.state.build] ?? this.kit.builds[0];
    const kit = kitOf(this.#fit());
    return this.kit.variants.find((v) => v.fit === kit && v.build === build) ?? null;
  }

  /** The cheapest variant of the current build, for "From $X" before a truck is picked. */
  #fromVariant() {
    const build = this.kit.builds[this.state.build] ?? this.kit.builds[0];
    return this.kit.variants.filter((v) => v.build === build).sort((a, b) => a.price - b.price)[0] ?? null;
  }

  #applyInitial() {
    const params = new URLSearchParams(window.location.search);
    const pack = PACKS[params.get('pk') ?? ''];
    if (pack !== undefined && pack < this.kit.builds.length) this.state.build = pack;

    // ?truck={fit handle}&year=2024 preselects a truck; the saved truck fills in otherwise
    // ?make=Ford&model=F-150 (Home truck cards) opens that model in its newest year with its shortest
    // bed there, the crew cab bed most of them have, and asks the shopper to check year and bed
    const fromUrl = this.fits.find((f) => f.handle === params.get('truck'));
    const modelFits = this.fits.filter((f) => f.make === params.get('make') && f.model === params.get('model'));
    const stored = getTruck();
    const storedFit = this.fits.find((f) => f.handle === stored?.fit);
    if (fromUrl && !params.get('year') && storedFit && storedFit.handle !== fromUrl.handle && kitOf(storedFit) === fromUrl.handle) {
      // A link to this kit keeps the saved older truck that ships it (a 2011 F-150 on the F-150 kit)
      this.#pick({ year: stored?.year, fit: storedFit.handle });
    } else if (fromUrl) {
      const year = params.get('year') ?? (stored?.fit === fromUrl.handle ? stored.year : '') ?? '';
      this.#pick({ year, fit: fromUrl.handle });
    } else if (modelFits.length) {
      const saved = modelFits.find((f) => f.handle === stored?.fit);
      if (saved) {
        this.#pick({ year: stored?.year, fit: saved.handle });
      } else {
        const year = modelFits.map((f) => defaultYear(f)).sort().at(-1) ?? '';
        const common = modelFits
          .filter((f) => yearsOf(f).includes(year))
          .sort((a, b) => (a.length ?? 0) - (b.length ?? 0))[0];
        this.#pick({ year, make: modelFits[0]?.make, model: modelFits[0]?.model, fit: common?.handle }, { store: false, guess: modelFits.length > 1 });
      }
    } else if (stored) {
      this.#pick({ year: stored.year, make: stored.make, model: stored.model, fit: stored.fit }, { store: false });
    } else {
      this.#pick({}, { store: false });
    }
  }

  /* ---------- Rendering ---------- */

  #t(key, values = {}) {
    let text = this.dataset[`t${key}`] ?? '';
    for (const [k, v] of Object.entries(values)) text = text.replaceAll(`[${k}]`, String(v));
    return text;
  }

  #money(cents, { trim = true } = {}) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, dec] = value.split('.');
    const commas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amount = trim && dec === '00' ? commas : `${commas}.${dec}`;
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, commas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/<[^>]+>/g, '');
  }

  #monthly(cents) {
    const min = Number(this.dataset.affirmMin);
    const months = Number(this.dataset.affirmMonths) || 12;
    if (min < 0 || cents < min) return null;
    return { amount: this.#money(Math.round(cents / months), { trim: false }), months };
  }

  #sizeKey(size) {
    if (/long/i.test(size)) return 'longbed';
    if (/^midsize/i.test(size)) return 'midsize';
    return 'fullsize';
  }

  #truckName(fit) {
    return /^\d/.test(fit.model) ? `${fit.make} ${fit.model}` : fit.model;
  }

  #render() {
    const fit = this.#fit();
    const variant = this.#variant();
    const ready = !!(fit && variant);
    const shown = variant ?? this.#fromVariant();
    const r = this.refs;

    this.dataset.ready = String(ready);
    this.dataset.size = fit ? this.#sizeKey(fit.size) : '';
    // A remembered truck shows its size photo on first paint, so don't leave that one lazy
    if (this.dataset.size) {
      const layer = this.querySelector(`[data-layer="${this.dataset.size}"] img[loading="lazy"]`);
      if (layer) layer.loading = 'eager';
    }

    // Price
    if (shown) {
      r.price.textContent = ready ? this.#money(shown.price) : this.#t('From', { price: this.#money(shown.price) });
      const save = shown.compare > shown.price ? shown.compare - shown.price : 0;
      if (r.compare) {
        r.compare.textContent = save ? this.#money(shown.compare) : '';
        r.compare.hidden = !save;
      }
      if (r.save) {
        r.save.textContent = save ? this.#t('Save', { amount: this.#money(save) }) : '';
        r.save.hidden = !save;
      }
      const monthly = this.#monthly(shown.price);
      if (r.monthly && monthly) r.monthly.textContent = monthly.amount;
      if (r.affirm) r.affirm.hidden = !monthly;
      if (r.ctaAffirm) {
        r.ctaAffirm.textContent = ready && monthly ? this.#t('AffirmLong', monthly) : '';
        r.ctaAffirm.hidden = !(ready && monthly);
      }
      if (r.stickyPrice) r.stickyPrice.textContent = r.price.textContent;
      if (r.stickyAffirm) r.stickyAffirm.textContent = monthly ? this.#t('AffirmShort', monthly) : '';
    }
    if (r.priceNote) {
      r.priceNote.textContent = fit ? this.#t('PriceNote', { size: fit.size }) : this.#t('PriceNoteEmpty');
    }

    // Fit line + hint
    if (r.fit && r.fitText) {
      r.fit.hidden = !fit;
      r.fitText.textContent = fit
        ? fit.bed
          ? this.#t('Fit', { bed: fit.bed, size: fit.size, width: fit.width, length: fit.length })
          : this.#t('FitNobed', { size: fit.size, width: fit.width, length: fit.length })
        : '';
    }
    if (r.hint) r.hint.hidden = !!fit;
    if (r.guess) r.guess.hidden = !(fit && this.#guess);
    r.config.classList.toggle('is-guess', !!fit && this.#guess);
    if (r.shown) r.shown.textContent = fit ? this.#t('Shown', { size: fit.size }) : r.shown.dataset.default ?? (r.shown.dataset.default = r.shown.textContent ?? '');

    // CTA
    const available = !variant || variant.available;
    r.ctaLabel.textContent = !ready
      ? this.#t('CtaPick')
      : available
        ? this.#t('CtaReady', { truck: this.#truckName(fit), price: this.#money(variant.price) })
        : this.#t('CtaUnavailable');
    r.cta.disabled = ready && !available;
    if (r.added && !ready) r.added.hidden = true;

    // Sticky bar copy
    if (r.stickySub) r.stickySub.textContent = fit ? truckLabel(fit, this.state.year) : this.#t('StickyEmpty');
    if (r.stickyCta) r.stickyCta.textContent = ready ? this.#t('StickyCtaReady') : this.#t('StickyCtaPick');

    this.#renderBuild();
    if (fit && r.live) r.live.textContent = `${r.fitText?.textContent ?? ''} ${r.price.textContent}`;
  }

  #renderBuild() {
    const note = this.querySelector('[data-build-note]');
    if (!note) return;
    const text = this.querySelector('[data-build-note-text]');
    const template = this.querySelector('template[data-build-notes]');
    const line = template instanceof HTMLTemplateElement
      ? template.content.querySelector(`[data-build="${this.state.build}"]`)?.textContent?.trim()
      : '';
    note.hidden = this.state.build === 0 || !line;
    if (text) text.textContent = line ?? '';
  }

  /* ---------- Cart ---------- */

  async #buy() {
    const fit = this.#fit();
    const variant = this.#variant();
    if (!fit || !variant) return this.#focusNext();
    const { cta, giftWrap, added, addedText, live } = this.refs;

    const lines = [{ id: variant.id, quantity: 1, properties: { Truck: truckLabel(fit, this.state.year) } }];
    const gift = /** @type {HTMLInputElement | null} */ (giftWrap?.querySelector('input:checked, input[data-gift-only]'));
    if (this.dataset.freeGift === 'true' && gift?.value) lines.push({ id: gift.value, quantity: 1, properties: { _gift: 'kit' } });

    cta.setAttribute('aria-busy', 'true');
    cta.disabled = true;
    const { ok, message } = await addLines(cta, lines, { sourceName: 'dialed-buy-module' });
    cta.removeAttribute('aria-busy');
    cta.disabled = false;

    const build = this.state.build ? this.kit.builds[this.state.build] : '';
    const text = ok
      ? build
        ? this.#t('AddedBuild', { build, truck: this.#truckName(fit) })
        : this.#t('Added', { truck: this.#truckName(fit) })
      : message || this.#t('Error');
    if (added && addedText) {
      addedText.textContent = text;
      added.hidden = false;
    }
    if (live) live.textContent = text;
  }

  #stickyBuy() {
    if (this.#variant()) return this.#buy();
    this.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    this.#focusNext();
  }

  #focusNext() {
    const { year, make, model, config } = this.refs;
    const next = !this.state.year ? year : !this.state.make ? make : !this.state.model ? model : config;
    next.focus({ preventScroll: false });
    try {
      next.showPicker?.();
    } catch {
      // showPicker needs a user gesture in some browsers
    }
  }

  /* ---------- Events from other sections ---------- */

  /** @param {Event} event */
  #onSelect = (event) => {
    const { year = '', make = '', model = '', fit = '' } = /** @type {CustomEvent} */ (event).detail ?? {};
    this.#pick({ year, make, model, fit });
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /** @param {Event} event */
  #onPack = (event) => {
    const pack = PACKS[/** @type {CustomEvent} */ (event).detail?.pack ?? ''];
    if (pack === undefined || pack >= this.kit.builds.length) return;
    this.state.build = pack;
    this.#render();
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const thumb = target?.closest('[data-slide]');
    if (thumb instanceof HTMLElement) this.#go(Number(thumb.dataset.slide));
    if (target?.closest('[data-switch-starter]')) {
      this.state.build = 0;
      this.#render();
    }
  };

  /* ---------- Gallery ---------- */

  #slides() {
    return [...this.refs.track.children];
  }

  #go(index) {
    const slides = this.#slides();
    if (!slides.length) return;
    this.#slide = Math.max(0, Math.min(index, slides.length - 1));
    this.refs.track.scrollTo({ left: this.#slide * this.refs.track.clientWidth, behavior: 'smooth' });
    this.#updateGallery();
  }

  #onTrackScroll = () => {
    const { track } = this.refs;
    const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    if (index !== this.#slide) {
      this.#slide = index;
      this.#updateGallery();
    }
  };

  #updateGallery() {
    const total = this.#slides().length;
    if (this.refs.count) this.refs.count.textContent = total > 1 ? `${this.#slide + 1} / ${total}` : '';
    if (this.refs.prev) this.refs.prev.disabled = this.#slide === 0;
    if (this.refs.next) this.refs.next.disabled = this.#slide >= total - 1;
    /** @type {HTMLElement | null} */
    let active = null;
    for (const thumb of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-slide]'))) {
      const current = Number(thumb.dataset.slide) === this.#slide;
      thumb.setAttribute('aria-current', String(current));
      if (current) active = thumb;
    }
    // Keep the current thumbnail in view when the strip scrolls (more than five photos)
    const strip = active?.parentElement;
    if (active && strip && strip.scrollWidth > strip.clientWidth + 1) {
      const left = active.offsetLeft - (strip.clientWidth - active.offsetWidth) / 2;
      strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
    }
  }

  /* ---------- Sticky bar ---------- */

  /** @type {number | null} */
  #stickyRaf = null;

  /**
   * Shows the bar once the CTA has scrolled above the viewport, hides it again near the footer.
   * Uses a capturing scroll listener (rAF-throttled) so it also hears Horizon's desktop scroll
   * container (.page-wrapper), and so jumps past the CTA without crossing it are caught.
   */
  #observeSticky() {
    const { sticky, cta } = this.refs;
    if (!sticky || this.dataset.sticky !== 'true') return;
    const footer = document.querySelector('.page-wrapper > footer, footer');
    const update = () => {
      this.#stickyRaf = null;
      const ctaAbove = cta.getBoundingClientRect().bottom < 0;
      const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      const show = ctaAbove && !footerVisible;
      if (sticky.hidden === show) sticky.hidden = !show;
    };
    const onScroll = () => {
      this.#stickyRaf ??= requestAnimationFrame(update);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true, signal: this.#abort.signal });
    window.addEventListener('resize', onScroll, { passive: true, signal: this.#abort.signal });
    update();
  }
}

if (!customElements.get('dialed-buy-module')) customElements.define('dialed-buy-module', DialedBuyModule);
